import * as React from 'react'
import WorkflowList from 'airship/widgets/new-push-popover/workflow-list'
import classnames = require('classnames')
import MessageType from './message-type'
import Modal from 'airship/patterns/modal'
import SvgSprite from 'airship/patterns/svg-sprite'

const BODY_CLASS = 'modal-open'

interface Props {
  hasPComposerAccess?: boolean
  hasExperimentsAccess?: boolean
  hasAppleNews?: boolean
  hasAutomationSeries?: boolean
  hasTriggersTag?: boolean
  hasInAppAutomation?: boolean
  createMessageUrl: string
  createExperimentUrl: string
  createAppleNewsUrl: string
  createAutomatedUrl: string
  createAutomationSeriesUrl: string
  createZoltarUrl: string
}

interface PageContext {
  has_automation_series_access: boolean
}

const NewPushPopover: React.FC<Props> = props => {
  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    if (document.body.classList) {
      document.body.classList.toggle(BODY_CLASS, open)
    }

    return () => {
      document.body.classList.remove(BODY_CLASS)
    }
  }, [open])

  return (
    <>
      {props.hasPComposerAccess && <CreateNewButton />}
      <Modal isOpen={open} onClose={toggle}>
        {props.hasPComposerAccess && (
          <React.Fragment>
            <h1 className="u-flex u-flex-justify-center u-text-center u-text-grow-6 u-text-bold u-bg-white u-size-full u-pad-top-6 u-space-top-6">
              What would you like to create?
            </h1>
            <StandardMessageTypes />
            <WorkflowList />
          </React.Fragment>
        )}
      </Modal>
    </>
  )

  function toggle(): void {
    setOpen(!open)
  }

  function StandardMessageTypes(): JSX.Element {
    return (
      <div className="u-contain-spread-sm u-cf">
        <div className="grid grid--with-gutter grid--align-center grid--equal-height u-space-top-6">
          <div className="grid-cell u-md-size-1of3 u-pad-top-1">
            <MessageType
              title="Message"
              url={props.createMessageUrl}
              spriteName="composer-message"
              seleniumTarget="composer-create-message"
              description="Send or schedule a message."
            />
          </div>
          {props.hasExperimentsAccess && (
            <div className="grid-cell u-md-size-1of3 u-pad-top-1">
              <MessageType
                title="A/B Test"
                url={props.createExperimentUrl}
                spriteName="composer-ab-test"
                description="Experiment with different variations of a message."
              />
            </div>
          )}
          {props.hasTriggersTag && (
            <div className="grid-cell u-md-size-1of3 u-pad-top-1">
              <MessageType
                title="Automation"
                url={props.createAutomatedUrl}
                spriteName="composer-automation"
                description="Automatically send a message to users who meet certain conditions."
              />
            </div>
          )}
          {props.hasAutomationSeries && (
            <div className="grid-cell u-md-size-1of3 u-pad-top-1">
              <MessageType
                title="Journey"
                url={props.createAutomationSeriesUrl}
                spriteName="composer-series"
                description="Automatically send a series of messages to users who meet certain conditions."
              />
            </div>
          )}
          {props.hasInAppAutomation && (
            <div className="grid-cell u-md-size-1of3 u-pad-top-1">
              <MessageType
                title="In-App Automation"
                url={props.createZoltarUrl}
                spriteName="composer-inapp-automation"
                description="Create a message that appears in your app when users meet certain conditions."
              />
            </div>
          )}
          {props.hasAppleNews && (
            <div className="grid-cell u-md-size-1of3 u-pad-top-1">
              <MessageType
                title="Apple News"
                url={props.createAppleNewsUrl}
                spriteName="composer-applenews"
                description="Send a push notification that links to an Apple News story."
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  function CreateNewButton(): JSX.Element {
    return (
      <a
        className={classnames('global-header__primary-action-link', {
          active: open
        })}
        href="#"
        rel="create-popover"
        onClick={toggle}
      >
        <SvgSprite name="add" />
        <span
          className="u-block u-text-uppercase u-text-shrink-3"
          data-selenium-target="composer-create-dropdown"
        >
          Create
        </span>
      </a>
    )
  }
}

export default NewPushPopover
